import { PermanentNumber } from '../../types/api';
import { baseApi } from './baseApi';

export interface RenewalPricing {
  baseCost: number;
  salePrice: number;
  duration: string;
  durationDays: number;
}

export interface PermanentService {
  id: string;
  name: string;
  capability?: string;
}

export interface PermanentLocation {
  areaCode: string;
  state: string;
  id: string;
  name: string;
}

export interface PermanentDurationOption {
  key: string;
  apiValue: string;
  days: number;
  label: string;
}

export interface GetPermanentServicesByServerResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    services: PermanentService[];
  };
}

export interface GetPermanentLocationsByServerResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    locationType: 'areaCodes';
    locations: PermanentLocation[];
  };
}

export interface GetPermanentDurationsByServerResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    autoRenewEnabled: boolean;
    durations: PermanentDurationOption[];
  };
}

export interface GetPermanentDurationsByServerParams {
  serverId: number;
  autoRenewEnabled: boolean;
}

export interface PermanentPurchaseSummaryRequest {
  serverId: number;
  serviceId: string;
  areaCode?: string;
  autoRenewEnabled?: boolean;
  durationKey?: string;
  stateCode?: string;
  ltrDuration?: number;
  capability?: string;
}
export interface PermanentPurchaseSummaryResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    service: {
      id: string;
      name: string;
    };
    areaCode?: string;
    autoRenewEnabled?: boolean;
    duration?: PermanentDurationOption;
    state?: {
      value: string;
      name: string;
      shortName: string;
    };
    ltrDuration?: number;
    baseCost?: number;
    price: number;
    currency: string;
  };
}

export interface PurchasePermanentNumberRequestCase1 {
  purchaseCase: 1;
  serviceId: string;
  stateCode: string;
  ltrDuration: 3 | 30;
  serviceName?: string;
  stateName?: string;
  capability?: string;
}

export interface PurchasePermanentNumberRequestCase2 {
  purchaseCase: 2;
  serviceId: string;
  areaCode?: string;
  autoRenewEnabled: boolean;
  durationKey: string;
  capability?: string;
}

export type PurchasePermanentNumberRequest =
  | PurchasePermanentNumberRequestCase1
  | PurchasePermanentNumberRequestCase2;

export interface PurchasePermanentNumberResponse {
  status: 'success';
  message: string;
  data: {
    permanentNumber?: PermanentNumber;
  } & PermanentNumber;
}

export interface GetPermanentNumberResponse {
  status: 'success';
  message: string;
  data: {
    permanentNumber: PermanentNumber;
  };
}

export type PermanentNumberStatus =
  | 'active'
  | 'expired'
  | 'refunded'
  | 'cancelled'
  | 'waiting';

export interface GetPermanentNumbersParams {
  status?: PermanentNumberStatus;
  page?: number;
  limit?: number;
}

interface GetPermanentNumbersApiResponse {
  status: 'success';
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    data: PermanentNumber[];
  };
}

export interface GetPermanentNumbersResponse {
  status: 'success';
  message: string;
  data: {
    numbers: PermanentNumber[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export const permanentNumbersApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPermanentServicesByServer: builder.query<GetPermanentServicesByServerResponse, { serverId: number }>({
      query: ({ serverId }) => ({
        url: `/permanent-numbers/servers/${serverId}/services`,
        method: 'GET',
      }),
      providesTags: ['PermanentNumbers'],
    }),

    getPermanentLocationsByServer: builder.query<GetPermanentLocationsByServerResponse, { serverId: number }>({
      query: ({ serverId }) => ({
        url: `/permanent-numbers/servers/${serverId}/locations`,
        method: 'GET',
      }),
      providesTags: ['PermanentNumbers'],
    }),

    getPermanentDurationsByServer: builder.query<
      GetPermanentDurationsByServerResponse,
      GetPermanentDurationsByServerParams
    >({
      query: ({ serverId, autoRenewEnabled }) => ({
        url: `/permanent-numbers/servers/${serverId}/durations?autoRenewEnabled=${autoRenewEnabled}`,
        method: 'GET',
      }),
      providesTags: ['PermanentNumbers'],
    }),

    getPermanentPurchaseSummary: builder.mutation<
      PermanentPurchaseSummaryResponse,
      PermanentPurchaseSummaryRequest
    >({
      query: (body) => ({
        url: '/permanent-numbers/purchase-summary',
        method: 'POST',
        body,
      }),
    }),

    purchasePermanentNumber: builder.mutation<
      PurchasePermanentNumberResponse,
      PurchasePermanentNumberRequest
    >({
      query: (body) => ({
        url: '/permanent-numbers/purchase',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PermanentNumbers', 'Balance', 'Transactions'],
    }),

    getRenewalPrice: builder.query<RenewalPricing, { permanentNumberId: string; duration: string }>({
      query: ({ permanentNumberId, duration }) => ({
        url: `/permanent-numbers/${permanentNumberId}/renew-pricing?duration=${duration}`,
        method: 'GET',
      }),
      transformResponse: (response: { status: string; data: RenewalPricing }) => response.data,
    }),

    renewPermanentNumber: builder.mutation<void, { permanentNumberId: string; extensionDuration?: string }>({
      query: ({ permanentNumberId, extensionDuration }) => ({
        url: `/permanent-numbers/${permanentNumberId}/renew`,
        method: 'POST',
        body: { extensionDuration },
      }),
      invalidatesTags: (_result, _error, { permanentNumberId }) => [
        { type: 'PermanentNumber', id: permanentNumberId },
        'PermanentNumbers',
        'Balance',
        'Transactions',
      ],
    }),

    getPermanentNumber: builder.query<GetPermanentNumberResponse, string>({
      query: (permanentNumberId) => ({
        url: `/permanent-numbers/${permanentNumberId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, permanentNumberId) => [
        { type: 'PermanentNumber', id: permanentNumberId },
      ],
    }),

    getPermanentNumbers: builder.query<
      GetPermanentNumbersResponse,
      GetPermanentNumbersParams
    >({
      query: ({ status, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());

        const queryString = params.toString();
        return {
          url: `/permanent-numbers${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      transformResponse: (response: GetPermanentNumbersApiResponse): GetPermanentNumbersResponse => {
        const pagination = response.data;
        const pages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 1)));
        return {
          status: response.status,
          message: response.message,
          data: {
            numbers: pagination.data,
            pagination: {
              page: pagination.page,
              limit: pagination.limit,
              total: pagination.total,
              pages,
            },
          },
        };
      },
      providesTags: ['PermanentNumbers'],
    }),

    activatePermanentNumber: builder.mutation<void, { permanentNumberId: string }>({
      query: ({ permanentNumberId }) => ({
        url: `/permanent-numbers/${permanentNumberId}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { permanentNumberId }) => [
        { type: 'PermanentNumber', id: permanentNumberId },
        'PermanentNumbers',
      ],
    }),

    sleepPermanentNumber: builder.mutation<void, { permanentNumberId: string }>({
      query: ({ permanentNumberId }) => ({
        url: `/permanent-numbers/${permanentNumberId}/sleep`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { permanentNumberId }) => [
        { type: 'PermanentNumber', id: permanentNumberId },
        'PermanentNumbers',
      ],
    }),

    refundPermanentNumber: builder.mutation<void, { permanentNumberId: string }>({
      query: ({ permanentNumberId }) => ({
        url: `/permanent-numbers/${permanentNumberId}/refund`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { permanentNumberId }) => [
        { type: 'PermanentNumber', id: permanentNumberId },
        'PermanentNumbers',
        'Balance',
        'Transactions',
      ],
    }),
  }),
});

export const {
  useGetPermanentServicesByServerQuery,
  useGetPermanentLocationsByServerQuery,
  useGetPermanentDurationsByServerQuery,
  useGetPermanentPurchaseSummaryMutation,
  usePurchasePermanentNumberMutation,
  useRenewPermanentNumberMutation,
  useGetPermanentNumberQuery,
  useGetPermanentNumbersQuery,
  useLazyGetRenewalPriceQuery,
  useActivatePermanentNumberMutation,
  useSleepPermanentNumberMutation,
  useRefundPermanentNumberMutation,
} = permanentNumbersApi;



